import * as STD                                         from '@emersya/mpa-std-lib';
import CM                                               from '@emersya/mpa-components-manager-lib';
import { InputComponent, IInputComponentConfiguration } from '../Input/Input';

export interface IInputNumberComponentConfiguration extends IInputComponentConfiguration<
	number,
	number
>
{
	accuracity? : number;
	step? : number;
	//use min or getMin
	min? : number;
	getMin? : CM.PropertyOperation.TDescriptor;
	//use max or getMax
	max? : number;
	getMax? : CM.PropertyOperation.TDescriptor;
	debouncerDelay? : number | null;
	roundingStrategy? : 'minimize' | 'maximize';
	/**
	 * Id of a standard rendering mode or path to an instance of InputNumberRenderingMode
	 * Default = 'textual'
	 */
	renderingMode? : (
		'slider' |
		'textual' |
		'textualWithIncrement' |
		CM.PropertyOperation.TDescriptor
		);
};
/**
 * Interface designed for allowing to have only one input-number class
 * that can have many different representation such as a textual input number or a slider.
 * The rendering mode is responsible for DOM generation / events dispatching etc...
 **/
export type TInputNumberRenderingMode = CM.Plugin.TPlugin<
	InputNumberComponent,
	{
		/**
		 * Called when min or max updated, stepsCountDiff = oldStepsCount - newStepsCounts
		 **/
		boundsUpdated(component : InputNumberComponent, stepsCountDiff : number) : void | Promise<void>;
	}
>;

export class InputNumberComponent extends InputComponent<
	HTMLDivElement,
	number,
	number,
	InputNumberComponent,
	NS.Configuration
>
{
	static readonly standardRenderingModes : {
		slider : SliderRenderingMode;
		textual : TextualRenderingMode;
		textualWithIncrement : TextualWithIncrementRenderingMode;
	} = <any> {};
	static count = 0;

	static override initialize() {
		InputComponent.initialize.call(this);
		this.standardRenderingModes.slider = new SliderRenderingMode;
		this.standardRenderingModes.textual = new TextualRenderingMode;
		this.standardRenderingModes.textualWithIncrement = new TextualWithIncrementRenderingMode;
		this.declareEventType('dragging');
		this.declareEventType('draggend');
	};

	renderingMode : TInputNumberRenderingMode;
	component_DOM : HTMLDivElement;
	step : number;
	roundingStrategy : 'minimize' | 'maximize';
	//use min or getMin
	getMin? : CM.PropertyOperation.TOperation;
	min? : number;
	//use max or getMax
	getMax? : CM.PropertyOperation.TOperation;
	max? : number;
	stepsCount : number = 1;

	override isValueTheDefault() {
		return (this.value === this.defaultValue);
	};

	async getMinimalValue(
		force                                                                    = false,
		event : STD.events.Event<CM.Component<any, any>, CM.Component<any, any>> = this.component_getEventWhichRepresentNoEvent()
	)
	{
		if (!force && this.currentMin) {
			return (this.currentMin);
		}
		let min : number;
		if (this.min !== undefined) {
			min = this.min;
		} else if (this.getMin) {
			min = await this.getMin.execute(this, event);
			if (typeof min !== 'number') {
				this.component_throwUserError('getMin not returned a number!', {
					min
				});
			}
		} else {
			return (-Infinity);
		}
		if (Math.abs(min) < this.step) {
			return (this.step * Math.sign(min));
		}
		if (this.roundingStrategy === 'minimize') {
			return (STD.math.floorToStep(min, this.step));
		} else {
			return (STD.math.ceilToStep(min, this.step));
		}
	};

	async getMaximalValue(
		force                                                 = false,
		event : STD.events.Event<any, CM.Component<any, any>> = this.component_getEventWhichRepresentNoEvent()
	)
	{
		if (!force && this.currentMax) {
			return (this.currentMax);
		}
		let max : number;
		if (this.max !== undefined) {
			max = this.max;
		} else if (this.getMax) {
			max = await this.getMax.execute(this, event);
			if (typeof max !== 'number') {
				this.component_throwUserError('getMax not returned a number!', {
					max
				});
			}
		} else {
			return (Infinity);
		}
		if (Math.abs(max) < this.step) {
			return (this.step * Math.sign(max));
		}
		return (STD.math.floorToStep(max, this.step));
	};

	override deserialize(x : number) {
		return (x);
	};

	override async component_start(startEvent : STD.events.Event<any, CM.Component<any, any>>) {
		if (this.getMin) {
			const getMinComponentDataSource = this.getMin.getComponent(this);
			if (getMinComponentDataSource && getMinComponentDataSource.component_hasValue()) {
				//bypassIfListenerDisabled
				getMinComponentDataSource.addEventListener('value', this.recomputeBounds.bind(this), {
					listener : this,
					subType :  '*'
				});
			}
		}
		if (this.getMax) {
			const getMaxComponentDataSource = this.getMax.getComponent(this);
			if (getMaxComponentDataSource && getMaxComponentDataSource.component_hasValue()) {
				getMaxComponentDataSource.addEventListener('value', this.recomputeBounds.bind(this), {
					listener : this,
					subType :  '*'
				});
			}
		}
		this.renderingMode.attach(this);
		await super.component_start(startEvent);
		if (!this.component_isDisabled) {
			await this.recomputeBounds(startEvent);
		}
	};

	isValidValue(n : number) {
		if (Number.isNaN(n)) {
			return (false);
		}
		if (this.currentMin > n) {
			return (false);
		}
		if (this.currentMax < n) {
			return (false);
		}
		return Math.round(new STD.math.BigNumber(n).modulo(new STD.math.BigNumber(this.step)).toNumber() * 1000000) /
			1000000 ===
			0;
	};

	getClosestInBoundValue(n : number) {
		const K = n / this.step;
		const [ nK1, nK2 ] = [ Math.floor(K), Math.ceil(K) ];
		if (nK1 === nK2) {
			return (n);
		}
		const [ d1, d2 ] = [ Math.abs(nK1 - K), Math.abs(nK2 - K) ];
		return (this.step * ((d1 < d2) ? nK1 : nK2));
	};

	currentMin : number = 0;
	currentMax : number = 0;

	async recomputeBounds(
		ev : STD.events.Event<any, CM.Component<any, any>> = this.component_getEventWhichRepresentNoEvent()
	)
	{
		if (
			!this.component_isStarted ||
			// (this.component_isDisabled && !this.component_isVisible) ||
			this.component_isDisabled ||
			(this.component_isDisabled && !this.isDefaultValueApplied)
		)
		{
			return;
		}
		const oldMin = this.currentMin;
		const oldMax = this.currentMax;
		const _max = await this.getMaximalValue(true, ev);
		const _min = await this.getMinimalValue(true, ev);
		const [ min, max ] = [ Math.min(_min, _max), Math.max(_min, _max) ];
		this.currentMin = min;
		this.currentMax = max;
		if ((oldMin === this.currentMin) && (oldMax === this.currentMax)) {
			if (this.value && this.isValidValue(this.value)) {
				return;
			}
		}
		const oldStepsCount = this.stepsCount;
		this.stepsCount = Math.floor((this.currentMax - this.currentMin) / this.step);
		if ((this.currentMin === -Infinity) || (this.currentMin === Infinity)) {
			this.component_throwUserError(
				'Minimum value should not be infinite!',
				{ min : this.currentMin }
			);
		}
		if ((this.currentMax === -Infinity) || (this.currentMax === Infinity)) {
			this.component_throwUserError(
				'Maximum value should not be infinite!',
				{ max : this.currentMax }
			);
		}
		let newValue = this.value;
		if (this.stepsCount < 0) {
			this.stepsCount = 0;
			newValue = this.currentMax - this.currentMin;
		} else {
			if (this.value === null) {
				this.component_throwLibraryError('!!');
			}
			newValue = this.getClosestInBoundValue(this.value);
		}
		newValue = Math.round(newValue * 1000000) / 1000000;
		if (newValue !== this.value) {
			await this.updateValue(newValue);
		}
		const diff = oldStepsCount - this.stepsCount;
		await this.renderingMode.boundsUpdated(this, diff);
	};

	async reset() {
		await this.updateValue(this.component_configuration.defaultValue || null);
	};

	protected override async component_onVisibilityUpdateAfterChilds(isVisible : boolean) {
		await super.component_onVisibilityUpdateAfterChilds(isVisible);
		if (this.value) {
			await this.recomputeBounds();
		}
	};

	serialize() {
		return this.value;
	};

	constructor(
		descriptor : ConstructorParameters<typeof CM.Component>[0],
		configuration : NS.Configuration
	)
	{
		super(descriptor, Object.assign(
			{
				defaultValue : 0,
				accuracity :   2,
				step :         1
			},
			configuration
		));
		if (
			(<IInputNumberComponentConfiguration> descriptor).defaultValue &&
			(typeof (<IInputNumberComponentConfiguration> descriptor).defaultValue !== 'number')
		)
		{
			throw new Error;
		}
		this.roundingStrategy = this.component_configuration.roundingStrategy ?? 'minimize';
		if (this.component_configuration.renderingMode && (typeof this.component_configuration.renderingMode !== 'string')) {
			const getRM = CM.PropertyOperation.create(
				this.component_configuration.renderingMode
			);
			const RM = getRM.execute(this, this.component_getEventWhichRepresentNoEvent());
			if (!(RM instanceof CM.Plugin)) {
				this.component_throwUserError('Invalid rendering mode', {
					renderingModePropertyPathValueGetter : getRM
				});
			}
			this.renderingMode = <any> RM;
		} else {
			this.renderingMode =
				InputNumberComponent.standardRenderingModes[ this.component_configuration.renderingMode ?? 'textual' ];
		}
		this.component_DOM = <HTMLDivElement> STD.DOM.factory({});
		this.step = <number> this.component_configuration.step;
		if (this.component_configuration.min !== undefined) {
			this.min = this.component_configuration.min;
		} else if (this.component_configuration.getMin) {
			this.getMin = CM.PropertyOperation.create(
				this.component_configuration.getMin
			);
			const component = this.getMin.getComponent(this);
			if (component && component.component_hasValue()) {
				//bypassIfListenerDisabled
				component.addEventListener('value', this.recomputeBounds.bind(this), {
					listener : this,
					subType :  '*'
				});
			}
		} else {
			this.min = 0;
		}
		if (this.component_configuration.max !== undefined) {
			this.max = this.component_configuration.max;
		} else if (this.component_configuration.getMax) {
			this.getMax = CM.PropertyOperation.create(
				this.component_configuration.getMax
			);
			const component = this.getMax.getComponent(this);
			if (component && component.component_hasValue()) {
				component.addEventListener('value', this.recomputeBounds.bind(this), {
					listener : this,
					subType :  '*'
				});
			}
		} else {
			this.max = 100;
		}
	};
};
export namespace NS {
	export const Component = InputNumberComponent;
	export type Component = InputNumberComponent;
	export type Configuration = IInputNumberComponentConfiguration;
	export type RenderingMode = TInputNumberRenderingMode;
}
CM.declare({
	id :   'input-number',
	type : InputNumberComponent
});
import { SliderRenderingMode }                          from './renderingModes/Slider';
import { TextualRenderingMode }                         from './renderingModes/Textual';
import { TextualWithIncrementRenderingMode }            from './renderingModes/TextualWithIncrement';
